import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { Badge, Button, Card } from '@/admin-design/_common';

function RepricePreviewCard({ data }: { data: any }) {
  const d = data;
  const dec = d.decision || {};
  const md: Array<any> = d.match_debug || [];

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <Card.Title>리프라이스 미리보기</Card.Title>
        <Card.Description>
          검색 → 매칭 → 가격 결정 결과 (DB 미저장).
        </Card.Description>
      </Card.Header>
      <div className="px-6 py-4 space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-muted-foreground">도매가 / 현재가</div>
            <div className="font-mono">
              {d.cost_price.toLocaleString()} / {d.current_price.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">매칭 통과</div>
            <div>{d.match_count}건 / 후보 {md.length}건</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">최저 lprice / 비교가</div>
            <div className="font-mono">
              {dec.lowest_lprice != null ? dec.lowest_lprice.toLocaleString() : '—'}
              {dec.lowest_compare != null && (
                <span className="text-muted-foreground"> / {dec.lowest_compare.toLocaleString()}</span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">{dec.lowest_seller || ''}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">결정</div>
            <div>
              {dec.apply ? (
                <Badge variant="success">apply → {dec.new_price?.toLocaleString()}</Badge>
              ) : (
                <Badge variant="warning">{dec.skip_reason || 'unknown'}</Badge>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="text-xs text-muted-foreground mb-1">검색어</div>
          <code className="block bg-muted px-2 py-1 rounded text-xs">{d.search_query || '(empty)'}</code>
        </div>

        <div>
          <div className="text-xs text-muted-foreground mb-1">검색 결과 평가 ({md.length}건)</div>
          {md.length === 0 ? (
            <div className="text-xs text-muted-foreground">검색 결과 없음</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left border-b">
                  <tr>
                    <th className="py-1 w-8">#</th>
                    <th>제목</th>
                    <th className="text-right pr-2">lprice</th>
                    <th>몰</th>
                    <th className="text-right pr-2">jaccard</th>
                    <th>brand</th>
                    <th className="text-right pr-2">score</th>
                    <th>판정</th>
                  </tr>
                </thead>
                <tbody>
                  {md.map((m, i) => (
                    <tr key={i} className={`border-b ${m.reason === 'passed' ? 'bg-success/5' : ''}`}>
                      <td className="py-1 text-muted-foreground">{i + 1}</td>
                      <td className="max-w-md truncate" title={m.title}>{m.title}</td>
                      <td className="text-right font-mono pr-2">{m.lprice.toLocaleString()}</td>
                      <td>{m.mall}</td>
                      <td className="text-right font-mono pr-2">{m.jaccard.toFixed(3)}</td>
                      <td>{m.brand_matched ? '✓' : '×'}</td>
                      <td className="text-right font-mono pr-2">{m.score.toFixed(3)}</td>
                      <td>
                        <Badge variant={m.reason === 'passed' ? 'success' : m.reason === 'self_store' ? 'muted' : 'warning'}>
                          {m.reason}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// 프로바이더별 원본 상품 URL 구성
function buildProviderURL(provider: string, productID: string): string {
  switch (provider) {
    case 'wsmon':
      return `https://www.wsmon.co.kr/shop/item.php?it_id=${encodeURIComponent(productID)}`;
    case 'upickb2b':
      return `https://upickb2b.com/product/detail.html?product_no=${encodeURIComponent(productID)}`;
    default:
      return '';
  }
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: p, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: () => api.product(id!),
  });

  type Action = 'suspend' | 'resume' | 'display_off' | 'display_on' | 'sold_out' | 'restock';

  const action = useMutation({
    mutationFn: (act: Action) => api.productsAction([Number(id)], act),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product', id] });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });

  const dbDelete = useMutation({
    mutationFn: () => api.deleteProducts([Number(id)]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      navigate('/products');
    },
  });

  const register = useMutation({
    mutationFn: () => api.createJob('register', { product_ids: [Number(id)], display_on: false }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['product', id] });
    },
  });

  const preview = useMutation({
    mutationFn: () => api.repricePreview(Number(id)),
  });

  const onPreview = () => {
    preview.mutate();
  };

  const onDbDelete = () => {
    if (!confirm('이 상품을 DB에서만 영구 삭제합니다.\n(네이버 등록 상태와 무관)')) return;
    dbDelete.mutate();
  };

  const onRegister = () => {
    if (!confirm('이 상품을 네이버에 등록합니다.\n(전시중지 상태로 등록되며, 작업 큐에 추가됩니다)')) return;
    register.mutate();
  };

  if (isLoading) return <div>로딩...</div>;
  if (!p) return <div>상품을 찾을 수 없습니다</div>;

  const onAction = (act: Action) => {
    const labels: Record<string, string> = {
      suspend: '판매중지', resume: '판매재개',
      display_off: '전시중지', display_on: '전시노출',
      sold_out: '품절', restock: '재입고',
    };
    if (!confirm(`이 상품을 "${labels[act]}" 처리할까요?\n(네이버에 등록된 경우 네이버 API도 호출)`)) return;
    action.mutate(act);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <Link to="/products" className="text-sm text-muted-foreground hover:underline">
          ← 목록으로
        </Link>
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-xs text-muted-foreground">판매:</span>
          <Button size="sm" variant="destructive" onClick={() => onAction('suspend')} loading={action.isPending}>판매중지</Button>
          <Button size="sm" variant="secondary" onClick={() => onAction('resume')} loading={action.isPending}>판매재개</Button>
          <span className="border-l border-border mx-1 h-5"></span>
          <span className="text-xs text-muted-foreground">전시:</span>
          <Button size="sm" variant="secondary" onClick={() => onAction('display_off')} loading={action.isPending}>전시중지</Button>
          <Button size="sm" variant="secondary" onClick={() => onAction('display_on')} loading={action.isPending}>전시노출</Button>
          <span className="border-l border-border mx-1 h-5"></span>
          <span className="text-xs text-muted-foreground">재고:</span>
          <Button size="sm" variant="secondary" onClick={() => onAction('sold_out')} loading={action.isPending}>품절</Button>
          <Button size="sm" variant="secondary" onClick={() => onAction('restock')} loading={action.isPending}>재입고</Button>
          <span className="border-l border-border mx-1 h-5"></span>
          <Button size="sm" variant="primary" onClick={onRegister} loading={register.isPending}>
            {register.isPending ? '등록 중...' : '네이버 등록'}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={onDbDelete}
            loading={dbDelete.isPending}
            className="text-destructive border-destructive-soft hover:bg-destructive-soft"
          >
            {dbDelete.isPending ? '삭제 중...' : 'DB 삭제'}
          </Button>
          <Button onClick={onPreview} loading={preview.isPending} disabled={preview.isPending} variant="secondary" size="sm">
            리프라이스 미리보기
          </Button>
        </div>
      </div>
      <h1 className="text-2xl font-bold">{p.name}</h1>
      <div className="space-y-1 text-xs text-muted-foreground">
        <div>
          <span className="inline-block w-10 text-muted-foreground">원본</span>
          <a
            href={buildProviderURL(p.provider, p.provider_product_id)}
            target="_blank"
            rel="noopener"
            className="text-foreground hover:underline font-mono"
          >
            {buildProviderURL(p.provider, p.provider_product_id)}
          </a>
        </div>
        {p.naver_product_url ? (
          <div>
            <span className="inline-block w-10 text-muted-foreground">네이버</span>
            <a
              href={p.naver_product_url}
              target="_blank"
              rel="noopener"
              className="text-success hover:underline font-mono"
            >
              {p.naver_product_url}
            </a>
            {p.naver_channel_product_no && (
              <span className="ml-2 text-muted-foreground">
                (ch#{p.naver_channel_product_no}
                {p.naver_origin_product_no ? `, origin#${p.naver_origin_product_no}` : ''})
              </span>
            )}
          </div>
        ) : (
          <div>
            <span className="inline-block w-10 text-muted-foreground">네이버</span>
            <span className="text-muted-foreground">미등록</span>
          </div>
        )}
      </div>

      {register.isSuccess && (
        <p className="text-sm text-success">
          등록 작업이 큐에 추가되었습니다 (job #{(register.data as any)?.id}).{' '}
          <Link to={`/logs?job_id=${(register.data as any)?.id}`} className="underline">
            로그 보기
          </Link>
        </p>
      )}
      {register.isError && (
        <p className="text-sm text-destructive">등록 실패: {(register.error as Error).message}</p>
      )}
      {action.isSuccess && (
        <p className="text-sm text-success">
          처리 완료: 네이버 {(action.data as any)?.naver_ok ?? 0}개 성공 / {(action.data as any)?.naver_fail ?? 0}개 실패
        </p>
      )}
      {action.isError && (
        <p className="text-sm text-destructive">실패: {(action.error as Error).message}</p>
      )}

      {preview.isError && (
        <Card className="shadow-sm">
          <Card.Header>
            <Card.Title>리프라이스 미리보기</Card.Title>
          </Card.Header>
          <div className="px-6 py-4 text-sm text-destructive">
            {(preview.error as Error)?.message || '미리보기 실패'}
          </div>
        </Card>
      )}

      {preview.data && <RepricePreviewCard data={preview.data} />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <Card.Body className="space-y-2">
            <h2 className="font-semibold">기본</h2>
            <dl className="grid grid-cols-[120px_1fr] gap-2 text-sm">
              <dt className="text-muted-foreground">ID</dt>
              <dd className="font-mono">{p.id}</dd>
              <dt className="text-muted-foreground">프로바이더</dt>
              <dd>{p.provider}</dd>
              <dt className="text-muted-foreground">상품 ID</dt>
              <dd className="font-mono text-xs">{p.provider_product_id}</dd>
              <dt className="text-muted-foreground">브랜드</dt>
              <dd>{p.brand || '-'}</dd>
              <dt className="text-muted-foreground">도매가</dt>
              <dd>{p.price.toLocaleString()}원</dd>
              <dt className="text-muted-foreground">노출가</dt>
              <dd>
                {p.expose_price.toLocaleString()}원
                {p.price > 0 &&
                  p.expose_price === Math.round((p.price * 1.3 + 50) / 100) * 100 && (
                    <span className="ml-2 text-xs text-muted-foreground">(도매가 +30% 자동 책정)</span>
                  )}
              </dd>
              <dt className="text-muted-foreground">도매 품절</dt>
              <dd>{p.sold_out ? '예' : '아니오'}</dd>
            </dl>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <h2 className="font-semibold mb-2">대표 이미지</h2>
            {p.main_image_url && (
              <a href={p.main_image_url} target="_blank" rel="noopener">
                <img
                  src={p.main_image_url}
                  alt={p.name}
                  className="w-full max-w-full h-auto object-contain rounded"
                  onError={(e) => {
                    // 404 등 깨졌을 때 상세이미지 첫 장으로 대체
                    try {
                      const details = JSON.parse(p.detail_image_urls || '[]');
                      const el = e.currentTarget as HTMLImageElement;
                      if (details.length > 0 && el.src !== details[0]) {
                        el.src = details[0];
                      } else {
                        el.style.display = 'none';
                      }
                    } catch {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }
                  }}
                />
              </a>
            )}
          </Card.Body>
        </Card>
      </div>

      <Card>
        <Card.Body>
          <h2 className="font-semibold mb-2">상세 설명</h2>
          <div
            className="prose max-w-none [&_img]:w-full [&_img]:max-w-xl [&_img]:h-auto [&_img]:mx-auto [&_img]:my-2"
            dangerouslySetInnerHTML={{ __html: p.detail_html }}
          />
        </Card.Body>
      </Card>
    </div>
  );
}
